/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { LegalPage, Section, BulletList } from "../components/LegalPage";

export default function AIDisclaimer() {
  return (
    <LegalPage
      title="AI Disclaimer"
      updated="26 February 2026"
      intro="The recommendations on ShouldIBuyIt.co.uk are produced by an artificial-intelligence model. Please read this disclaimer so you understand the strengths and limits of that approach."
    >
      <Section title="1. What the AI does">
        <p>
          When you submit a query, your text is sent to a large language model (LLM) operated by Anthropic, Inc.
          The model draws on its training data — books, websites, product reviews, and consumer reporting — to
          identify a product that, in the model's assessment, best fits the use case you described. It also
          generates the supporting text: the verdict, the "why this is my pick" paragraphs, the pros and cons,
          and the alternatives.
        </p>
      </Section>

      <Section title="2. What the AI is not">
        <BulletList items={[
          "It is not a human expert. It has not personally used the products it recommends.",
          "It is not connected to live Amazon stock, pricing or review data. Prices shown are guidance based on the model's training and are not live quotes.",
          "It is not a professional adviser. It does not provide regulated financial, medical, legal, safety, dietary, accessibility or child-safety advice.",
          "It is not infallible. AI models can hallucinate product names, mis-attribute features or be out of date about a brand's current model line-up.",
        ]} />
      </Section>

      <Section title="3. How to use a recommendation safely">
        <BulletList items={[
          "Always verify the product on Amazon — read the live listing, check current price and stock, read the most recent reviews.",
          "If the recommendation matters for safety, health, accessibility or your finances, seek qualified human advice in addition.",
          "If a recommendation seems off (an unfamiliar model number, a price that looks wrong, a brand that does not appear to make that category), trust your instinct and ask a follow-up question or run a fresh search.",
          "Tell us when a recommendation is unhelpful — the thumbs-down and 'did you buy it?' buttons feed back into our quality reviews.",
        ]} />
      </Section>

      <Section title="4. Specifications, prices and availability">
        <p>
          Product specifications, model variants, prices and availability change frequently. ShouldIBuyIt.co.uk
          does not have a live feed of Amazon's catalogue. The price shown on a recommendation card is a typical
          UK price, clearly marked with a tilde (~) and the caption "Price guide only — live price may vary on
          Amazon." Always check the Amazon listing before purchasing.
        </p>
      </Section>

      <Section title="5. No liability for AI errors">
        <p>
          We do not warrant that any AI-generated recommendation is accurate, complete, suitable, current or
          fit for any particular purpose. To the extent permitted by law, we disclaim liability for any loss
          arising from reliance on an AI-generated recommendation. See the full liability terms in the{" "}
          <Link to="/terms" className="text-emerald-500 underline">Terms &amp; Conditions</Link>.
        </p>
      </Section>

      <Section title="6. AI processing and your data">
        <p>
          Please do not include personal information (names, addresses, financial details, health details,
          children's details) in your queries. Anything you submit is forwarded to the AI provider to generate
          the response. The data we hold and how we handle it is described in our{" "}
          <Link to="/privacy" className="text-emerald-500 underline">Privacy Policy</Link>.
        </p>
      </Section>

      <Section title="7. Reporting an inaccurate recommendation">
        <p>
          Spotted a recommendation that mis-names a product, suggests an item that does not exist, or makes a
          claim that looks wrong? Please email{" "}
          <a className="text-emerald-500 underline" href="mailto:hello@shouldibuyit.co.uk">hello@shouldibuyit.co.uk</a>{" "}
          with the question you asked and what you saw. Reports go straight to the quality review team and
          materially help future recommendations.
        </p>
      </Section>
    </LegalPage>
  );
}
